import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import { FilterList, KeyboardArrowDown, KeyboardArrowRight, SwapHoriz } from "@mui/icons-material";
import type { Dayjs } from "dayjs";
import type { Hierarchy, createFlashService } from "@/api/flash.service";
import { getUserCompanies } from "@/api/user.service";
import type { Company } from "@/api/company.service";
import { formatCurrency, formatPercentage } from "@/utils/format";
import { FilterDrawer } from "./FilterDrawer";
import { VIEW_TABS } from "./types";
import type { CatFlatRow, DiarioEntry, SortKey, TableRow, ViewType } from "./types";
import { buildCatFlatRows, sortRows, sumToTotRow, toRow } from "./utils";

type FlashService = ReturnType<typeof createFlashService>;

interface FlashComparativoProps {
  flashService: FlashService;
  clientId: string;
  userId: string;
  currentDate: Dayjs;
}

function ValCell({ value, bold = false, divider = false }: { value: number; bold?: boolean; divider?: boolean }) {
  return (
    <TableCell
      align="right"
      sx={{
        color: value < 0 ? "error.main" : "text.primary",
        fontWeight: bold ? 700 : 400,
        whiteSpace: "nowrap",
        ...(divider && { borderLeft: "1px solid", borderColor: "divider" }),
      }}
    >
      {formatCurrency(value)}
    </TableCell>
  );
}

function PctCell({ value, bold = false, divider = false }: { value: number; bold?: boolean; divider?: boolean }) {
  return (
    <TableCell
      align="right"
      sx={{
        color: value >= 0 ? "primary.main" : "error.main",
        fontWeight: bold ? 700 : 400,
        whiteSpace: "nowrap",
        ...(divider && { borderLeft: "1px solid", borderColor: "divider" }),
      }}
    >
      {formatPercentage(value)}
    </TableCell>
  );
}

export function FlashComparativo({ flashService, clientId, userId, currentDate }: FlashComparativoProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [hierarchyData, setHierarchyData] = useState<Hierarchy[]>([]);
  const [categories, setCategories] = useState<Hierarchy[]>([]);
  const [loadingBase, setLoadingBase] = useState(true);

  const [viewType, setViewType] = useState<ViewType>("lojas");
  const [showMeta, setShowMeta] = useState(true);
  const [showAnt, setShowAnt] = useState(true);
  const [showAno, setShowAno] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("atual");
  const [sortAsc, setSortAsc] = useState(false);
  const [visaoAxis, setVisaoAxis] = useState<"loja_x_cat" | "cat_x_loja">("loja_x_cat");

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedLojas, setSelectedLojas] = useState<Set<string>>(new Set());
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());

  const [expandedCatRows, setExpandedCatRows] = useState<Set<string>>(new Set());
  const [expandedFilterCats, setExpandedFilterCats] = useState<Set<string>>(new Set());
  const [catSubData, setCatSubData] = useState<Record<string, Hierarchy[]>>({});
  const [loadingCatSubRows, setLoadingCatSubRows] = useState<Record<string, boolean>>({});
  const [catSubLojaData, setCatSubLojaData] = useState<Record<string, Hierarchy[]>>({});

  const [visaoData, setVisaoData] = useState<Record<string, Hierarchy[]>>({});
  const [loadingVisao, setLoadingVisao] = useState(false);
  const [diarioData, setDiarioData] = useState<DiarioEntry[]>([]);
  const [loadingDiario, setLoadingDiario] = useState(false);

  const dateYMD = currentDate.format("YYYYMMDD");
  const dateISO = currentDate.format("YYYY-MM-DD");

  const resolveCompanyName = (cod: string, fallback: string) => companies.find((c) => c.erp_code === cod)?.name ?? fallback;

  useEffect(() => {
    getUserCompanies(userId).then((data) => setCompanies(data.filter((c) => c.client_id === clientId)));
  }, [userId, clientId]);

  useEffect(() => {
    setLoadingBase(true);
    Promise.all([flashService.getHierarchy(dateYMD, clientId), flashService.getCategorias(dateISO, null, null, clientId)])
      .then(([hierarchy, cats]) => {
        setHierarchyData(hierarchy);
        setCategories(cats.filter((c) => !c.is_total && c.nomeempresaapp));
      })
      .finally(() => setLoadingBase(false));
    setExpandedCatRows(new Set());
    setExpandedFilterCats(new Set());
    setCatSubData({});
    setLoadingCatSubRows({});
    setCatSubLojaData({});
    setVisaoData({});
  }, [dateYMD, dateISO, clientId]);

  const lojas = useMemo(() => hierarchyData.filter((i) => !i.is_total && i.nomeempresaapp), [hierarchyData]);

  const lojaKey = lojas.map((l) => l.cod_empresa).join(",");
  useEffect(() => {
    if (lojas.length > 0) setSelectedLojas(new Set(lojas.map((l) => l.cod_empresa)));
  }, [lojaKey]);

  const catKey = categories.map((c) => c.cod_empresa).join(",");
  useEffect(() => {
    if (categories.length > 0) setSelectedCats(new Set(categories.map((c) => c.cod_empresa)));
  }, [catKey]);

  // Visão por loja (categorias top-level de cada loja) — usada pela aba "Por Loja" (quando há filtro
  // de categoria ativo) e pela aba "Visão".
  useEffect(() => {
    if ((viewType !== "lojas" && viewType !== "visao") || lojas.length === 0) return;
    setLoadingVisao(true);
    Promise.all(
      lojas.map((loja) =>
        flashService
          .getCategorias(dateISO, loja.cod_empresa, null, clientId)
          .then((res) => ({ code: loja.cod_empresa, data: res.filter((c) => !c.is_total && c.nomeempresaapp) })),
      ),
    )
      .then((results) => {
        const map: Record<string, Hierarchy[]> = {};
        results.forEach((r) => { map[r.code] = r.data; });
        setVisaoData(map);
      })
      .finally(() => setLoadingVisao(false));
  }, [viewType, dateISO, lojaKey, clientId]);

  // ponytail: sem o modo "rápido" (flash_diario) usado no mobile só para 2 clientes com feature-flag —
  // aqui sempre reconstruímos o dia a partir da hierarquia diária (N requisições por mês). Trocar para o
  // endpoint dedicado se isso ficar lento em produção.
  useEffect(() => {
    if (viewType !== "diario") return;
    setLoadingDiario(true);
    const start = currentDate.startOf("month");
    const days = Array.from({ length: currentDate.date() }, (_, d) => start.add(d, "day"));
    Promise.all(days.map((d) => flashService.getHierarchy(d.format("YYYYMMDD"), clientId)))
      .then((results) => {
        const entries: DiarioEntry[] = results.map((items, idx) => {
          const prev = idx > 0 ? results[idx - 1] : null;
          const byLoja: DiarioEntry["byLoja"] = {};
          items.filter((i) => !i.is_total && i.nomeempresaapp).forEach((item) => {
            const prevItem = prev?.find((p) => !p.is_total && p.cod_empresa === item.cod_empresa);
            const daily = prevItem ? item.mes_atual - prevItem.mes_atual : item.mes_atual;
            // meta_mes é acumulada até a data consultada (dia 01 = meta só do dia 01), então a meta do dia é a
            // diferença para o dia anterior, igual à venda
            const dailyMeta = prevItem ? item.meta_mes - prevItem.meta_mes : item.meta_mes;
            const difAbsoluto = daily - dailyMeta;
            const dif = dailyMeta > 0 ? ((daily - dailyMeta) / dailyMeta) * 100 : 0;
            const pctAtingMes = item.meta_mes > 0 ? (item.mes_atual / item.meta_mes) * 100 : 0;
            byLoja[item.cod_empresa] = { atual: daily, meta: dailyMeta, difAbsoluto, dif, pctAtingMes, vendaAcumulada: item.mes_atual };
          });
          return { label: days[idx]!.format("DD/MM"), isToday: idx === currentDate.date() - 1, byLoja };
        });
        setDiarioData(entries);
      })
      .finally(() => setLoadingDiario(false));
  }, [viewType, dateYMD, clientId]);

  const fetchSubCatsPerLoja = (parentCode: string) => {
    const missing = lojas.filter((l) => catSubLojaData[`${l.cod_empresa}|${parentCode}`] === undefined);
    missing.forEach(async (loja) => {
      try {
        const res = await flashService.getCategorias(dateISO, loja.cod_empresa, parentCode, clientId);
        setCatSubLojaData((prev) => ({ ...prev, [`${loja.cod_empresa}|${parentCode}`]: res.filter((c) => c.nomeempresaapp) }));
      } catch {
        // ignore — linha permanece com dado bruto do nível superior
      }
    });
  };

  const fetchSubCats = async (parentCode: string) => {
    if (catSubData[parentCode] !== undefined) {
      fetchSubCatsPerLoja(parentCode);
      return;
    }
    setLoadingCatSubRows((prev) => ({ ...prev, [parentCode]: true }));
    try {
      const res = await flashService.getCategorias(dateISO, null, parentCode, clientId);
      const children = res.filter((c) => c.nomeempresaapp);
      setCatSubData((prev) => ({ ...prev, [parentCode]: children }));
      setSelectedCats((prev) => {
        if (!prev.has(parentCode)) return prev;
        const next = new Set(prev);
        children.forEach((c) => next.add(c.cod_empresa));
        return next;
      });
      fetchSubCatsPerLoja(parentCode);
    } finally {
      setLoadingCatSubRows((prev) => ({ ...prev, [parentCode]: false }));
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((p) => !p);
    else { setSortKey(key); setSortAsc(false); }
  };

  const filteredLojas = lojas.filter((l) => selectedLojas.has(l.cod_empresa));
  const filteredCats = categories.filter((c) => selectedCats.has(c.cod_empresa));

  // ── Por Loja ───────────────────────────────────────────────────────────────
  const noCatFilter = categories.length > 0 && categories.every((c) => selectedCats.has(c.cod_empresa));
  const lojaRowSource: TableRow[] = filteredLojas.map((loja) => {
    const nome = resolveCompanyName(loja.cod_empresa, loja.nomeempresaapp);
    if (noCatFilter || Object.keys(visaoData).length === 0) return { ...toRow(loja), nome };

    const activeCats = categories.filter((c) => selectedCats.has(c.cod_empresa));
    let atual = 0, meta = 0, ant = 0, ano = 0;
    activeCats.forEach((c) => {
      const entry = visaoData[loja.cod_empresa]?.find((x) => x.cod_empresa === c.cod_empresa);
      if (entry) { atual += entry.mes_atual; meta += entry.meta_mes; ant += entry.mes_anterior; ano += entry.ano_anterior; }
    });
    return {
      key: `${loja.cod_empresa}-${nome}`, nome, atual, meta, ant, ano,
      difMeta: meta > 0 ? ((atual - meta) / meta) * 100 : 0,
      difAnt: ant > 0 ? ((atual - ant) / ant) * 100 : 0,
      difAno: ano > 0 ? ((atual - ano) / ano) * 100 : 0,
    };
  });
  const lojaRows = sortRows(lojaRowSource, sortKey, sortAsc);

  // ── Por Categoria ────────────────────────────────────────────────────────
  const flatCatRows = buildCatFlatRows(categories, expandedCatRows, catSubData, loadingCatSubRows, sortKey, sortAsc);
  const visibleCatRows = flatCatRows.filter((row) => selectedCats.has(row.item.cod_empresa));

  const computeCatRow = (item: Hierarchy, parentCatCode: string | null): TableRow => {
    const lojaFilterActive = selectedLojas.size < lojas.length;
    if (!lojaFilterActive) return toRow(item);

    const dataAvailable = parentCatCode === null
      ? Object.keys(visaoData).length > 0
      : lojas.some((l) => catSubLojaData[`${l.cod_empresa}|${parentCatCode}`] !== undefined);
    if (!dataAvailable) return toRow(item);

    const selectedLojasList = lojas.filter((l) => selectedLojas.has(l.cod_empresa));
    let atual = 0, meta = 0, ant = 0, ano = 0;
    for (const loja of selectedLojasList) {
      const source = parentCatCode === null
        ? visaoData[loja.cod_empresa]?.find((c) => c.cod_empresa === item.cod_empresa)
        : catSubLojaData[`${loja.cod_empresa}|${parentCatCode}`]?.find((c) => c.cod_empresa === item.cod_empresa);
      atual += source?.mes_atual ?? 0;
      meta += source?.meta_mes ?? 0;
      ant += source?.mes_anterior ?? 0;
      ano += source?.ano_anterior ?? 0;
    }
    return {
      key: `${item.cod_empresa}-${item.nomeempresaapp}`, nome: item.nomeempresaapp, atual, meta, ant, ano,
      difMeta: meta > 0 ? ((atual - meta) / meta) * 100 : 0,
      difAnt: ant > 0 ? ((atual - ant) / ant) * 100 : 0,
      difAno: ano > 0 ? ((atual - ano) / ano) * 100 : 0,
    };
  };

  const toggleCatRow = (row: CatFlatRow) => {
    if (!row.canExpand) return;
    const next = new Set(expandedCatRows);
    if (next.has(row.key)) next.delete(row.key);
    else { next.add(row.key); fetchSubCats(row.item.cod_empresa); }
    setExpandedCatRows(next);
  };

  // ── Visão (matriz) ───────────────────────────────────────────────────────
  const visaoLojas = filteredLojas;
  const visaoCats = filteredCats.length > 0 ? filteredCats : categories;
  const getCatVal = (lojaCode: string, catCode: string) => visaoData[lojaCode]?.find((c) => c.cod_empresa === catCode)?.mes_atual ?? 0;
  const getCatMeta = (lojaCode: string, catCode: string) => visaoData[lojaCode]?.find((c) => c.cod_empresa === catCode)?.meta_mes ?? 0;
  const calcDif = (val: number, meta: number) => (meta > 0 ? ((val - meta) / meta) * 100 : 0);

  const axisRows = visaoAxis === "loja_x_cat"
    ? visaoLojas.map((l) => ({ key: l.cod_empresa, label: resolveCompanyName(l.cod_empresa, l.nomeempresaapp) }))
    : visaoCats.map((c) => ({ key: c.cod_empresa, label: c.nomeempresaapp }));
  const axisCols = visaoAxis === "loja_x_cat"
    ? visaoCats.map((c) => ({ key: c.cod_empresa, label: c.nomeempresaapp }))
    : visaoLojas.map((l) => ({ key: l.cod_empresa, label: resolveCompanyName(l.cod_empresa, l.nomeempresaapp) }));

  const cellVal = (rowKey: string, colKey: string) => {
    const [lojaCode, catCode] = visaoAxis === "loja_x_cat" ? [rowKey, colKey] : [colKey, rowKey];
    return { val: getCatVal(lojaCode, catCode), meta: getCatMeta(lojaCode, catCode) };
  };

  // ── Header comum das tabelas Por Loja / Por Categoria ───────────────────
  const renderSortHeader = (label: string, key: SortKey, align: "left" | "right" = "right") => (
    <TableCell align={align} sortDirection={sortKey === key ? (sortAsc ? "asc" : "desc") : false}>
      <TableSortLabel active={sortKey === key} direction={sortAsc ? "asc" : "desc"} onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderDataHeaderRow = () => (
    <MuiTableRow>
      {renderSortHeader("Loja/Categoria", "nome", "left")}
      {renderSortHeader("Atual", "atual")}
      {showMeta && renderSortHeader("Meta", "meta")}
      {showMeta && renderSortHeader("Dif% Meta", "difMeta")}
      {showAnt && renderSortHeader("Mês Ant.", "ant")}
      {showAnt && renderSortHeader("Dif% Ant.", "difAnt")}
      {showAno && renderSortHeader("Ano Ant.", "ano")}
      {showAno && renderSortHeader("Dif% Ano", "difAno")}
    </MuiTableRow>
  );

  const renderDataCells = (row: TableRow, bold = false) => (
    <>
      <ValCell value={row.atual} bold={bold} />
      {showMeta && <ValCell value={row.meta} bold={bold} />}
      {showMeta && <PctCell value={row.difMeta} bold={bold} />}
      {showAnt && <ValCell value={row.ant} bold={bold} />}
      {showAnt && <PctCell value={row.difAnt} bold={bold} />}
      {showAno && <ValCell value={row.ano} bold={bold} />}
      {showAno && <PctCell value={row.difAno} bold={bold} />}
    </>
  );

  const isLoading =
    loadingBase ||
    ((viewType === "lojas" || viewType === "visao") && loadingVisao) ||
    (viewType === "diario" && loadingDiario);

  const showCatFilter = (viewType === "lojas" || viewType === "cats" || viewType === "visao") && categories.length > 0;

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
        <Tabs value={VIEW_TABS.findIndex((t) => t.key === viewType)} onChange={(_, idx) => setViewType(VIEW_TABS[idx]?.key ?? "lojas")}>
          {VIEW_TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">Comparar:</Typography>
          {viewType !== "diario" && (
            <>
              <Chip label="Ano Ant." size="small" color={showAno ? "primary" : "default"} onClick={() => setShowAno((v) => !v)} />
              <Chip label="Mês Ant." size="small" color={showAnt ? "primary" : "default"} onClick={() => setShowAnt((v) => !v)} />
            </>
          )}
          <Chip label="Meta" size="small" color={showMeta ? "primary" : "default"} onClick={() => setShowMeta((v) => !v)} />
          <IconButton size="small" onClick={() => setFilterOpen(true)}>
            <FilterList fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {isLoading ? (
        <LinearProgress />
      ) : viewType === "lojas" ? (
        lojaRows.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>Sem dados disponíveis.</Typography>
        ) : (
          <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>{renderDataHeaderRow()}</TableHead>
              <TableBody>
                {lojaRows.map((r) => (
                  <MuiTableRow key={r.key} hover>
                    <TableCell>{r.nome}</TableCell>
                    {renderDataCells(r)}
                  </MuiTableRow>
                ))}
                <MuiTableRow sx={{ bgcolor: "background.subtle" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  {renderDataCells(sumToTotRow(lojaRows), true)}
                </MuiTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : viewType === "cats" ? (
        visibleCatRows.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>Sem dados disponíveis.</Typography>
        ) : (
          <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>{renderDataHeaderRow()}</TableHead>
              <TableBody>
                {visibleCatRows.map((row) => {
                  const rowData = computeCatRow(row.item, row.parentCatCode);
                  return (
                    <MuiTableRow key={row.key} hover>
                      <TableCell sx={{ pl: 2 + row.depth * 2 }}>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          {row.canExpand ? (
                            <IconButton size="small" onClick={() => toggleCatRow(row)} sx={{ p: 0.25 }}>
                              {row.isLoadingChildren ? (
                                <Box sx={{ width: 16 }} />
                              ) : row.isExpanded ? (
                                <KeyboardArrowDown fontSize="small" />
                              ) : (
                                <KeyboardArrowRight fontSize="small" />
                              )}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 28 }} />
                          )}
                          {row.item.nomeempresaapp}
                        </Stack>
                      </TableCell>
                      {renderDataCells(rowData)}
                    </MuiTableRow>
                  );
                })}
                <MuiTableRow sx={{ bgcolor: "background.subtle" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  {renderDataCells(sumToTotRow(visibleCatRows.filter((r) => r.depth === 0).map((r) => computeCatRow(r.item, r.parentCatCode))), true)}
                </MuiTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : viewType === "visao" ? (
        !axisRows.length || !axisCols.length ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>Sem dados disponíveis.</Typography>
        ) : (
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ width: "fit-content", cursor: "pointer" }} onClick={() => setVisaoAxis((p) => (p === "loja_x_cat" ? "cat_x_loja" : "loja_x_cat"))}>
              <SwapHoriz fontSize="small" color="primary" />
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                Linhas: {visaoAxis === "loja_x_cat" ? "Loja" : "Categoria"} · trocar eixo
              </Typography>
            </Stack>
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              {/* ponytail: border-collapse:collapse (MUI default) + rowSpan header is a known Chromium bug that renders stray/duplicate border hairlines — separate collapse avoids it */}
              <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <TableHead>
                  <MuiTableRow>
                    <TableCell rowSpan={2}>{visaoAxis === "loja_x_cat" ? "Loja" : "Categoria"}</TableCell>
                    {axisCols.map((c) => (
                      <TableCell key={c.key} align="center" colSpan={showMeta ? 2 : 1}>{c.label}</TableCell>
                    ))}
                    <TableCell align="center" colSpan={showMeta ? 2 : 1} sx={{ borderLeft: "1px solid", borderColor: "divider" }}>Total</TableCell>
                  </MuiTableRow>
                  <MuiTableRow>
                    {axisCols.map((c) => (
                      <Fragment key={c.key}>
                        <TableCell align="right">Atual</TableCell>
                        {showMeta && <TableCell align="right">Dif%</TableCell>}
                      </Fragment>
                    ))}
                    <TableCell align="right" sx={{ borderLeft: "1px solid", borderColor: "divider" }}>Atual</TableCell>
                    {showMeta && <TableCell align="right">Dif%</TableCell>}
                  </MuiTableRow>
                </TableHead>
                <TableBody>
                  {axisRows.map((row) => {
                    let rowTotal = 0, rowMeta = 0;
                    return (
                      <MuiTableRow key={row.key} hover>
                        <TableCell>{row.label}</TableCell>
                        {axisCols.map((col) => {
                          const { val, meta } = cellVal(row.key, col.key);
                          rowTotal += val;
                          rowMeta += meta;
                          return (
                            <Fragment key={col.key}>
                              <ValCell value={val} />
                              {showMeta && <PctCell value={calcDif(val, meta)} />}
                            </Fragment>
                          );
                        })}
                        <ValCell value={rowTotal} bold />
                        {showMeta && <PctCell value={calcDif(rowTotal, rowMeta)} bold />}
                      </MuiTableRow>
                    );
                  })}
                  <MuiTableRow sx={{ bgcolor: "background.subtle" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    {axisCols.map((col) => {
                      const colTotal = axisRows.reduce((s, row) => s + cellVal(row.key, col.key).val, 0);
                      const colMeta = axisRows.reduce((s, row) => s + cellVal(row.key, col.key).meta, 0);
                      return (
                        <Fragment key={col.key}>
                          <ValCell value={colTotal} bold />
                          {showMeta && <PctCell value={calcDif(colTotal, colMeta)} bold />}
                        </Fragment>
                      );
                    })}
                    <ValCell
                      value={axisRows.reduce((s, row) => s + axisCols.reduce((s2, col) => s2 + cellVal(row.key, col.key).val, 0), 0)}
                      bold
                    />
                    {showMeta && (
                      <PctCell
                        value={calcDif(
                          axisRows.reduce((s, row) => s + axisCols.reduce((s2, col) => s2 + cellVal(row.key, col.key).val, 0), 0),
                          axisRows.reduce((s, row) => s + axisCols.reduce((s2, col) => s2 + cellVal(row.key, col.key).meta, 0), 0),
                        )}
                        bold
                      />
                    )}
                  </MuiTableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )
      ) : diarioData.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>Sem dados disponíveis.</Typography>
      ) : (
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <TableHead>
              <MuiTableRow>
                <TableCell rowSpan={2}>Dia</TableCell>
                {visaoLojas.map((l) => (
                  <TableCell key={l.cod_empresa} align="center" colSpan={showMeta ? 6 : 5} sx={{ borderLeft: "1px solid", borderColor: "divider" }}>
                    {resolveCompanyName(l.cod_empresa, l.nomeempresaapp)}
                  </TableCell>
                ))}
              </MuiTableRow>
              <MuiTableRow>
                {visaoLojas.map((l) => (
                  <Fragment key={l.cod_empresa}>
                    {showMeta && <TableCell align="right" sx={{ borderLeft: "1px solid", borderColor: "divider" }}>Meta</TableCell>}
                    <TableCell align="right" sx={!showMeta ? { borderLeft: "1px solid", borderColor: "divider" } : undefined}>Venda</TableCell>
                    <TableCell align="right">Dif $</TableCell>
                    <TableCell align="right">% var.</TableCell>
                    <TableCell align="right">% Ating.</TableCell>
                    <TableCell align="right">Venda Acum.</TableCell>
                  </Fragment>
                ))}
              </MuiTableRow>
            </TableHead>
            <TableBody>
              {diarioData.map((entry) => (
                <MuiTableRow key={entry.label} hover selected={entry.isToday}>
                  <TableCell sx={{ fontWeight: entry.isToday ? 700 : 400 }}>{entry.label}</TableCell>
                  {visaoLojas.map((l) => {
                    const d = entry.byLoja[l.cod_empresa] ?? { atual: 0, meta: 0, difAbsoluto: 0, dif: 0, pctAtingMes: 0, vendaAcumulada: 0 };
                    return (
                      <Fragment key={l.cod_empresa}>
                        {showMeta && <ValCell value={d.meta} divider />}
                        <ValCell value={d.atual} divider={!showMeta} />
                        <ValCell value={d.difAbsoluto} />
                        <PctCell value={d.dif} />
                        <PctCell value={d.pctAtingMes} />
                        <ValCell value={d.vendaAcumulada} />
                      </Fragment>
                    );
                  })}
                </MuiTableRow>
              ))}
              <MuiTableRow sx={{ bgcolor: "background.subtle" }}>
                <TableCell sx={{ fontWeight: 700 }}>Acum.</TableCell>
                {visaoLojas.map((l) => {
                  const a = diarioData.reduce(
                    (acc, entry) => {
                      const d = entry.byLoja[l.cod_empresa];
                      if (!d) return acc;
                      return { atual: acc.atual + d.atual, meta: acc.meta + d.meta, pctAtingMes: d.pctAtingMes, vendaAcumulada: d.vendaAcumulada };
                    },
                    { atual: 0, meta: 0, pctAtingMes: 0, vendaAcumulada: 0 },
                  );
                  const difAbsoluto = a.atual - a.meta;
                  const dif = a.meta > 0 ? ((a.atual - a.meta) / a.meta) * 100 : 0;
                  return (
                    <Fragment key={l.cod_empresa}>
                      {showMeta && <ValCell value={a.meta} bold divider />}
                      <ValCell value={a.atual} bold divider={!showMeta} />
                      <ValCell value={difAbsoluto} bold />
                      <PctCell value={dif} bold />
                      <PctCell value={a.pctAtingMes} bold />
                      <ValCell value={a.vendaAcumulada} bold />
                    </Fragment>
                  );
                })}
              </MuiTableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        lojas={lojas}
        categories={categories}
        showCatFilter={showCatFilter}
        selectedLojas={selectedLojas}
        setSelectedLojas={setSelectedLojas}
        selectedCats={selectedCats}
        setSelectedCats={setSelectedCats}
        expandedFilterCats={expandedFilterCats}
        setExpandedFilterCats={setExpandedFilterCats}
        catSubData={catSubData}
        loadingCatSubRows={loadingCatSubRows}
        fetchSubCats={fetchSubCats}
        resolveCompanyName={resolveCompanyName}
      />
    </Stack>
  );
}
